/**
 * Unicode superscript and subscript conversion for the Superscript & Subscript Generator.
 *
 * Two behaviours per direction:
 *
 * - `all` converts every character that has a small form (digits, + - = ( ) and the
 *   letters Unicode defines), which is right for ordinals (st, nd) and short exponents.
 * - `digits` converts only digits and charges, leaving letters and operators alone, which
 *   is what chemical formulas (Fe2O3, Ca(OH)2, Fe3+) and exponents inside a sentence
 *   (x2, E=mc2) need.
 *
 * Every code point is from the Unicode Superscripts and Subscripts block
 * (U+2070-U+209F), Latin-1 (U+00B2, U+00B3, U+00B9) and Phonetic Extensions; the hyphen
 * and the true minus sign (U+2212) both map to SUPERSCRIPT/SUBSCRIPT MINUS (U+207B/U+208B).
 */

const SUPERSCRIPT_MAP: Record<string, string> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
  "+": "⁺",
  "-": "⁻",
  "\u2212": "⁻",
  "=": "⁼",
  "(": "⁽",
  ")": "⁾",
  a: "ᵃ",
  b: "ᵇ",
  c: "ᶜ",
  d: "ᵈ",
  e: "ᵉ",
  f: "ᶠ",
  g: "ᵍ",
  h: "ʰ",
  i: "ⁱ",
  j: "ʲ",
  k: "ᵏ",
  l: "ˡ",
  m: "ᵐ",
  n: "ⁿ",
  o: "ᵒ",
  p: "ᵖ",
  r: "ʳ",
  s: "ˢ",
  t: "ᵗ",
  u: "ᵘ",
  v: "ᵛ",
  w: "ʷ",
  x: "ˣ",
  y: "ʸ",
  z: "ᶻ",
  A: "ᴬ",
  B: "ᴮ",
  D: "ᴰ",
  E: "ᴱ",
  G: "ᴳ",
  H: "ᴴ",
  I: "ᴵ",
  J: "ᴶ",
  K: "ᴷ",
  L: "ᴸ",
  M: "ᴹ",
  N: "ᴺ",
  O: "ᴼ",
  P: "ᴾ",
  R: "ᴿ",
  T: "ᵀ",
  U: "ᵁ",
  V: "ⱽ",
  W: "ᵂ",
};

const SUBSCRIPT_MAP: Record<string, string> = {
  "0": "₀",
  "1": "₁",
  "2": "₂",
  "3": "₃",
  "4": "₄",
  "5": "₅",
  "6": "₆",
  "7": "₇",
  "8": "₈",
  "9": "₉",
  "+": "₊",
  "-": "₋",
  "\u2212": "₋",
  "=": "₌",
  "(": "₍",
  ")": "₎",
  a: "ₐ",
  e: "ₑ",
  h: "ₕ",
  i: "ᵢ",
  j: "ⱼ",
  k: "ₖ",
  l: "ₗ",
  m: "ₘ",
  n: "ₙ",
  o: "ₒ",
  p: "ₚ",
  r: "ᵣ",
  s: "ₛ",
  t: "ₜ",
  u: "ᵤ",
  v: "ᵥ",
  x: "ₓ",
};

export type ScriptMode = "all" | "digits";

const DIGIT = /\d/;
const LETTER = /\p{L}/u;
const SIGNS = new Set(["+", "-", "\u2212"]);
const COEFFICIENT_NEXT = /[\p{Lu}([]/u;
const FORMULA_PREV = /[\p{L})\]]/u;

function convertWithMap(text: string, map: Record<string, string>): string {
  return [...text].map((char) => map[char] ?? char).join("");
}

const WHITESPACE = /\s/;

/**
 * A sign is a charge or an exponent sign (Fe3+, 10-3, -3) unless it joins two terms
 * (x2+y2, a - b): it must touch what it belongs to, and not run into a letter or bracket.
 */
function isRaisedSign(
  previous: string | undefined,
  next: string | undefined
): boolean {
  const attached = previous !== undefined && !WHITESPACE.test(previous);
  if (next !== undefined && (LETTER.test(next) || next === "(")) {
    return false;
  }
  if (attached) {
    return true;
  }
  return next !== undefined && DIGIT.test(next);
}

function superscriptDigits(text: string): string {
  const chars = [...text];
  return chars
    .map((char, index) => {
      if (DIGIT.test(char)) {
        return SUPERSCRIPT_MAP[char] ?? char;
      }
      if (SIGNS.has(char) && isRaisedSign(chars[index - 1], chars[index + 1])) {
        return SUPERSCRIPT_MAP[char] ?? char;
      }
      return char;
    })
    .join("");
}

/**
 * Lowers the digits of a formula. A number in front of a capital letter or bracket and
 * not after a letter or closing bracket is a coefficient (the 2 in 2H2O), so it stays.
 */
function subscriptDigits(text: string): string {
  const chars = [...text];
  let out = "";
  let index = 0;
  while (index < chars.length) {
    if (!DIGIT.test(chars[index])) {
      out += chars[index];
      index += 1;
      continue;
    }
    let end = index;
    while (end < chars.length && DIGIT.test(chars[end])) {
      end += 1;
    }
    const run = chars.slice(index, end).join("");
    const previous = chars[index - 1];
    const next = chars[end];
    const follows = previous !== undefined && FORMULA_PREV.test(previous);
    const coefficient =
      !follows && next !== undefined && COEFFICIENT_NEXT.test(next);
    out += coefficient ? run : convertWithMap(run, SUBSCRIPT_MAP);
    index = end;
  }
  return out;
}

export function toSuperscript(text: string, mode: ScriptMode = "all"): string {
  return mode === "digits"
    ? superscriptDigits(text)
    : convertWithMap(text, SUPERSCRIPT_MAP);
}

export function toSubscript(text: string, mode: ScriptMode = "all"): string {
  return mode === "digits"
    ? subscriptDigits(text)
    : convertWithMap(text, SUBSCRIPT_MAP);
}
